import {
  TemplateMessageDataDto,
  TemplateMessageRequestDto,
  TemplateMessageResponseDto,
} from '@src/data/types/TemplateMessageResponseDto'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface UnreadEntry {
  creditApplicationId: string
  financingProgramId: string
}

export interface SignalRConnectionInfo {
  url: string
  accessToken: string
}

const CHAT_MESSAGES_URL = '/ChatMessages'
const TEMPLATES_URL = '/ChatTemplateMessages'
const SCOPE = 'templateMessages'

// Query Keys Factory
export const chatMessageKeysFactory = {
  all: [SCOPE] as const,
  unreadEntries: () => [SCOPE, 'unread-entries'] as const,
  list: () => [SCOPE, 'list'] as const,
}

// GET unread entries
const getUnreadEntries = async (): Promise<UnreadEntry[]> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<UnreadEntry[]>(`${CHAT_MESSAGES_URL}/Unread/entries`)
  return response.data
}

export function useUnreadEntries(enabled: boolean): [UnreadEntry[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: chatMessageKeysFactory.unreadEntries(),
    queryFn: () => getUnreadEntries(),
    enabled,
  })
  return [data ?? [], isFetching]
}

// SignalR negotiate
export const negotiate = async (): Promise<SignalRConnectionInfo> => {
  const response = await getApiClient().post<SignalRConnectionInfo>(`${TEMPLATES_URL}/negotiate`)
  return response.data
}

// SignalR join group
export const joinAllMessagesGroup = async (
  connectionId: string,
  merchantId: string,
  isAgent: boolean,
): Promise<void> => {
  await getApiClient().post(`${TEMPLATES_URL}/management/joinAllMessagesGroup`, {
    connectionId,
    merchantId,
    isAgent,
  })
}

export const getTemplateMessagesAndPlaceholderskeys = async (): Promise<TemplateMessageDataDto> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<TemplateMessageDataDto>(`${TEMPLATES_URL}`)
  return response.data ?? { messages: [], placeholders: [] }
}

export const useGetTemplateMessagesAndPlaceholderskeys = (): [TemplateMessageDataDto, boolean] => {
  const { data, isFetching } = useQuery({
    queryKey: chatMessageKeysFactory.list(),
    queryFn: () => getTemplateMessagesAndPlaceholderskeys(),
  })
  return [data ?? { messages: [], placeholders: [] }, isFetching]
}

type UpsertMessageFn = MutationFunction<TemplateMessageResponseDto, TemplateMessageRequestDto>


const upsertTemplateMessage: UpsertMessageFn = async (data) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<TemplateMessageResponseDto>(
    `${TEMPLATES_URL}/createOrUpdate`,
    data,
  )
  return response.data
}

export const useUpsertTemplateMessage = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: upsertTemplateMessage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatMessageKeysFactory.all })
    },
  })

  return [mutateAsync, isPending, reset] as const
}


export const deleteTemplateMessage = async (code: string): Promise<void> => {
  const apiClient = getApiClient()
  await apiClient.delete(`${TEMPLATES_URL}/${code}`)
}

export const useDeleteTemplateMessage = () => {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: deleteTemplateMessage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chatMessageKeysFactory.all })
    },
  })

  return [mutateAsync, isPending, reset] as const
}
